/**
 * Make a file actually arrive on the user's machine.
 *
 * Every download in this system was written the same way, and the same two
 * details were wrong everywhere:
 *
 *   1. The anchor was not in the document. Chromium ignores a click on a
 *      detached element, so the code ran, no file appeared, and nothing threw.
 *      The button looked like it worked.
 *   2. The object URL was revoked on the next line. That races the download the
 *      click just started, so the same code worked on one machine and failed on
 *      another — which is worse than failing everywhere, because it looks fixed.
 *
 * Both are invisible in review and invisible to an API check. They are only
 * visible when somebody clicks the button, which is why they survived to a
 * customer demo. Everything that saves a file should go through here.
 */

/** How long the blob URL stays alive after the click. */
const REVOKE_AFTER_MS = 10_000

function sanitizeFileName(name: string): string {
  const cleaned = name.trim().replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ')
  return cleaned || 'download'
}

/**
 * Click a download link the way browsers actually require.
 *
 * `revokeUrl` is the caller's object URL, if it made one; it is released on a
 * timer rather than immediately.
 */
export function triggerDownload(href: string, fileName: string, revokeUrl?: string): void {
  const link = document.createElement('a')
  link.href = href
  link.download = sanitizeFileName(fileName)
  link.style.display = 'none'
  link.rel = 'noopener'

  document.body.appendChild(link)
  link.click()
  link.remove()

  if (revokeUrl) {
    window.setTimeout(() => URL.revokeObjectURL(revokeUrl), REVOKE_AFTER_MS)
  }
}

/** Save data the app already holds. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  triggerDownload(url, fileName, url)
}

/** Save text the app already holds, as a file of the given type. */
export function downloadText(
  content: string,
  fileName: string,
  mimeType = 'text/plain;charset=utf-8;',
): void {
  downloadBlob(new Blob([content], { type: mimeType }), fileName)
}
